import { useOktaAuth } from "@okta/okta-react";
import {
  createContext,
  ReactElement,
  useContext,
  useMemo,
  useState,
} from "react";
import { RBACRole } from "../../api/rbac/types";
import LocalStorage from "../../utils/LocalStorage";
import SessionStorage from "../../utils/SessionStorage";
import { ConfigContext } from "../ConfigContext";

export interface DelegateContextProps {
  isDelegating: boolean;
  delegatedUserId?: string;
  setDelegatedUser: (xId: string) => void;
  clearDelegatedUser: () => void;
}

export const ROLES_WITH_DELEGATION_ACCESS = [
  RBACRole.JD_ADMIN,
  RBACRole.JD_SUPER_ADMIN,
  RBACRole.JD_DEVELOPER_ADMIN,
];

export const RESTRICTED_DELEGATION_TARGET_ROLES = [
  RBACRole.JD_ADMIN,
  RBACRole.JD_SUPER_ADMIN,
  RBACRole.JD_READ_ONLY,
  RBACRole.JD_DEVELOPER_ADMIN,
];

export const DelegateContext = createContext<DelegateContextProps>({
  isDelegating: false,
  setDelegatedUser: () => {},
  clearDelegatedUser: () => {},
});

export const useDelegateContext = () => useContext(DelegateContext);

export const useEffectiveUserId = (): string => {
  const { authState } = useOktaAuth();
  const { ENABLE_DELEGATE_LOGIN } = useContext(ConfigContext);
  const { isDelegating, delegatedUserId } = useContext(DelegateContext);
  const actorUserId = authState?.accessToken?.claims?.userID as string;

  return ENABLE_DELEGATE_LOGIN && isDelegating ? delegatedUserId! : actorUserId;
};

export const DelegateProvider = ({ children }: { children: ReactElement }) => {
  const { ENABLE_DELEGATE_LOGIN } = useContext(ConfigContext);
  const [delegatedUserId, setDelegatedUserId] = useState<string | undefined>(
    SessionStorage.getDelegatedUserId() ?? undefined
  );

  const setDelegatedUser = (xId: string) => {
    SessionStorage.setDelegatedUserId(xId);
    setDelegatedUserId(xId);
  };

  const clearDelegatedUser = () => {
    SessionStorage.removeDelegatedUserId();
    LocalStorage.setJSONValue("jdRoleActivity", {
      lastVisitedAccountId: "",
    });
    setDelegatedUserId(undefined);
  };

  const value: DelegateContextProps = useMemo(
    () =>
      ENABLE_DELEGATE_LOGIN
        ? {
            isDelegating: Boolean(delegatedUserId),
            delegatedUserId,
            setDelegatedUser,
            clearDelegatedUser,
          }
        : {
            isDelegating: false,
            setDelegatedUser: () => {},
            clearDelegatedUser: () => {},
          },
    [ENABLE_DELEGATE_LOGIN, delegatedUserId]
  );

  return (
    <DelegateContext.Provider value={value}>
      {children}
    </DelegateContext.Provider>
  );
};
